/**
 * The console's loading skeletons (spec §1.2, §1.3).
 *
 * ## A skeleton exists to hold a shape, not to look busy
 *
 * The defect this module was written for is a LAYOUT JUMP, not a missing
 * spinner. `AdoptionPanels` fetched separately and rendered `null` while it
 * waited, so the Brief painted its work panels at ~836ms and the adoption
 * blocks dropped in at ~1199ms, shoving everything below them down the page.
 * That is the "old layout for a second, then it snaps" the review reported.
 *
 * So the ONE property every skeleton here must have is a real, declared
 * height. A skeleton that collapses to nothing is not a lighter version of
 * the fix — it is the bug it exists to prevent, wearing the fix's name. Each
 * block below therefore takes an explicit pixel height, and the callers build
 * their skeletons out of the SAME layout constants their loaded markup uses,
 * so the two cannot drift apart silently.
 *
 * ## The shimmer is optional; the space is not
 *
 * `.skel` (declared once, in `main.tsx`) paints the sheen, and it is declared
 * inside `@media (prefers-reduced-motion: no-preference)`. A reader who has
 * asked their system for less motion gets a still block of exactly the same
 * size — the reservation is in the inline style, never in the animation. A
 * page with no stylesheet at all (a unit test rendering a view straight into
 * happy-dom) likewise still reserves the space, because the background colour
 * and the height are inline.
 */

import { CHAT_INPUT_STYLE } from "./ChatInputChrome";
import { C, DS, RT } from "../tokens";
import { useMediaQuery } from "../shell/useMediaQuery";
import {
  columnWidth, HOME_TOP, ITEM_DETAIL_COLUMNS, PHOTO_TILE_MIN, TEAM_PAGE, WALK_COLUMNS,
  RECORDS_COMPACT_QUERY, RECORDS_FILTER_BAR, RECORDS_HEADER_ROW, RECORDS_ICON_BUTTON, RECORDS_ROW, RECORDS_TABLE_END, RECORDS_TOP_BAR,
} from "../layout";

/** The console's own rounding for a placeholder block. */
const RADIUS = 8;

/**
 * One placeholder block, of a stated size.
 *
 * `height` is required and has no default on purpose: a caller that forgot it
 * would render a zero-height skeleton, which is precisely the failure this
 * module exists to make impossible, and a default would hide the omission.
 */
export function SkeletonBlock({
  height,
  width = "100%",
  radius = RADIUS,
  style,
  testId,
}: {
  height: number | string;
  width?: number | string;
  radius?: number;
  style?: React.CSSProperties;
  testId?: string;
}) {
  return (
    <div
      className="skel"
      aria-hidden
      data-testid={testId}
      style={{
        height,
        width,
        maxWidth: "100%",
        borderRadius: radius,
        background: C.sunk,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

/** A stack of identical placeholder rows — a list, before it is a list. */
export function SkeletonRows({
  rows,
  height,
  gap = 8,
  testId,
}: {
  rows: number;
  height: number;
  gap?: number;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{ display: "flex", flexDirection: "column", gap }}
    >
      {Array.from({ length: rows }, (_, index) => (
        <SkeletonBlock key={index} height={height} />
      ))}
    </div>
  );
}

/** The card shell a loaded panel will fill, with its rows already sized. */
export function SkeletonCard({ rows, rowHeight = 46 }: { rows: number; rowHeight?: number }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 16,
        padding: "19px 21px",
        boxShadow: `0 1px 2px ${C.line}`,
      }}
    >
      <SkeletonBlock height={10} width={110} radius={5} style={{ marginBottom: 14 }} />
      <SkeletonRows rows={rows} height={rowHeight} />
    </div>
  );
}

/** The four stat tiles a stats grid will fill, at the tile's real height. */
export function SkeletonTiles({ count, height }: { count: number; height: number }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(170px, 1fr))`,
        gap: 12,
      }}
    >
      {Array.from({ length: count }, (_, index) => (
        <SkeletonBlock key={index} height={height} radius={12} />
      ))}
    </div>
  );
}

/**
 * What a whole view looks like before its data lands.
 *
 * Full screens select a named layout. The older coarse shapes remain for
 * generic embedded callers. Shared widths and grids live in layout.ts so a
 * loading screen cannot silently use a different column from its content.
 */
export type ViewSkeletonShape = "rows" | "tiles" | "detail" | "home" | "photos" | "people" | "settings" | "policies" | "invite" | "member" | "project" | "project-brief" | "walk" | "item" | "items" | "items-flow" | "records";

export function ViewSkeleton({ shape = "rows" }: { shape?: ViewSkeletonShape }) {
  if (shape === "records") return <RecordsSkeleton />;
  if (!["rows", "tiles", "detail"].includes(shape)) return <ScreenSkeleton shape={shape} />;
  return (
    <div
      data-testid="loading-state"
      data-skeleton={shape}
      role="status"
      aria-label="Loading content"
      aria-busy="true"
      style={{ flex: 1, minHeight: 0, overflow: "hidden", padding: "22px 28px 0" }}
    >
      {/* The header every view shares: crumb, then title. */}
      <SkeletonBlock height={9} width={190} radius={5} />
      <SkeletonBlock height={24} width={300} style={{ marginTop: 10, marginBottom: 26 }} />

      {shape === "tiles" && (
        <>
          <SkeletonTiles count={4} height={100} />
          <div style={{ height: 26 }} />
          <SkeletonCard rows={4} />
        </>
      )}

      {shape === "detail" && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr)", gap: 16 }}>
          <div>
            <SkeletonBlock height={150} radius={12} style={{ marginBottom: 14 }} />
            <SkeletonCard rows={3} rowHeight={36} />
          </div>
          <SkeletonCard rows={5} rowHeight={34} />
        </div>
      )}

      {shape === "rows" && (
        <>
          <SkeletonBlock height={10} width={120} radius={5} style={{ marginBottom: 12 }} />
          <SkeletonCard rows={6} />
        </>
      )}
    </div>
  );
}

function Lines({ title = true }: { title?: boolean }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <SkeletonBlock height={12} width={190} />
    {title && <SkeletonBlock height={29} width="45%" />}
  </div>;
}

function Pills({ count = 3 }: { count?: number }) {
  return <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
    {Array.from({ length: count }, (_, i) => <SkeletonBlock key={i} height={32} width={112} radius={999} />)}
  </div>;
}

export function RecentWalksSkeleton() {
  return <div data-skeleton="recent-walks" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    <SkeletonBlock height={20} width={130} />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
      {[0, 1, 2].map((i) => <SkeletonBlock key={i} height={112} radius={16} />)}
    </div>
  </div>;
}

export function ProjectsSkeleton() {
  // This is mounted inside Home's centred header, whose cross-axis alignment
  // is `center`. Give the skeleton the same full-width contract as the real
  // project strip; otherwise the flex item shrink-wraps to one card and the
  // loading cards stack despite the loaded grid being three columns.
  return <div data-skeleton="projects" style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
    <SkeletonBlock height={20} width={170} />
    <div
      data-testid="skeleton-project-grid"
      style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(max(190px, calc((100% - 20px) / 3)), 1fr))", gap: 10 }}
    >
      {[0, 1, 2].map((i) => <SkeletonBlock key={i} height={118} radius={14} />)}
    </div>
  </div>;
}

function TableSkeleton({ grouped = false }: { grouped?: boolean }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: grouped ? 22 : 0 }}>
    {Array.from({ length: grouped ? 3 : 1 }, (_, group) => <div key={group}>
      {grouped && <SkeletonBlock height={10} width={100} style={{ marginBottom: 9 }} />}
      <div style={{ background: C.surface, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.line}` }}>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.line}` }}><SkeletonBlock height={9} width="80%" /></div>
        {Array.from({ length: grouped ? 2 : 5 }, (_, row) => <div key={row} style={{ height: 52, padding: "12px 20px", display: "flex", alignItems: "center", gap: 16, borderBottom: `1px solid ${C.line}` }}>
          <SkeletonBlock height={12} width={22} /><SkeletonBlock height={12} width="45%" />
          <SkeletonBlock height={12} width="15%" /><SkeletonBlock height={12} width="10%" />
        </div>)}
      </div>
    </div>)}
  </div>;
}

/**
 * Punch, Observations and Autodesk Issues: the records table
 * (`views/records/RecordsWorkspace.tsx`). It is full-bleed on the pane's white,
 * so this one is too: the top bar (title, view pills, Export, New), the filter
 * bar (Filter, search, count, layout switch) and a table running edge to edge
 * from under the bars to the composer's rail, all off the constants the loaded
 * table uses. The centred column the other list views share would draw the
 * page the redesign replaced and then snap to this one.
 */
function RecordsSkeleton() {
  const compact = useMediaQuery(RECORDS_COMPACT_QUERY);
  const bar = { flex: "none", display: "flex", alignItems: "center", borderBottom: `1px solid ${DS.line}` } as const;
  return <div data-testid="loading-state" data-skeleton="records"
    role="status" aria-label="Loading content" aria-busy="true"
    style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column", background: RT.pane }}>
    <div data-testid="skeleton-column" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div data-testid="skeleton-records-topbar" style={{ ...bar, gap: 10, ...RECORDS_TOP_BAR }}>
        <SkeletonBlock height={16} width={112} />
        <span style={{ flex: "none", width: 1, height: 18, background: DS.line, margin: "0 4px" }} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
          {[52, 62, 112].map((width, i) => <SkeletonBlock key={i} height={30} width={width} />)}
        </div>
        <SkeletonBlock height={32} width={compact ? RECORDS_ICON_BUTTON : 104} />
        <SkeletonBlock height={32} width={compact ? RECORDS_ICON_BUTTON : 150} />
      </div>
      <div data-testid="skeleton-records-filterbar" style={{ ...bar, gap: 8, ...RECORDS_FILTER_BAR, flexWrap: "wrap" }}>
        <SkeletonBlock height={30} width={76} />
        <SkeletonBlock height={30} width="auto" style={{ flexGrow: 0, flexShrink: 1, flexBasis: 220, minWidth: 120 }} />
        <span style={{ flex: 1 }} />
        <SkeletonBlock height={10} width={42} />
        <SkeletonBlock height={30} width={206} />
      </div>
      <div data-testid="skeleton-records-table" style={{ flex: 1, minHeight: 0, overflow: "hidden", marginBottom: RECORDS_TABLE_END, borderBottom: "1px solid rgba(10,10,10,0.08)" }}>
        <div style={{ height: RECORDS_HEADER_ROW + 1, display: "flex", alignItems: "center", gap: 28, padding: "0 14px", background: RT.strip, borderBottom: "1px solid rgba(10,10,10,0.08)" }}>
          {["24%", "12%", "14%", "9%", "12%", "12%"].map((width, i) => <SkeletonBlock key={i} height={10} width={width} />)}
        </div>
        {Array.from({ length: 8 }, (_, row) => <div key={row} style={{ height: RECORDS_ROW + 1, display: "flex", alignItems: "center", gap: 28, padding: "0 14px", borderBottom: "1px solid rgba(10,10,10,0.06)" }}>
          {["24%", "12%", "14%", "9%", "12%", "12%"].map((width, i) => <SkeletonBlock key={i} height={12} width={width} />)}
        </div>)}
      </div>
    </div>
  </div>;
}

/** Match the screen's hierarchy and real width, not a generic loading card.
 * Row counts and text lengths are intentionally unknown until data arrives. */
function ScreenSkeleton({ shape }: { shape: ViewSkeletonShape }) {
  const home = shape === "home";
  const item = shape === "item";
  const walk = shape === "walk";
  const people = shape === "people";
  const items = shape === "items" || shape === "items-flow";
  const photos = shape === "photos";
  const wide = photos || item ? "record" : items ? "table" : undefined;
  return <div className="sb" data-testid="loading-state" data-skeleton={shape}
    role="status" aria-label="Loading content" aria-busy="true"
    style={{ flex: 1, minHeight: 0, overflow: "hidden", paddingTop: home ? HOME_TOP : walk || people ? 0 : item ? 26 : 22,
      ...(home ? { scrollbarGutter: "stable" } : {}) }}>
    {walk && <div style={{ height: 52, padding: "18px 48px", borderBottom: `1px solid ${C.line}` }}><SkeletonBlock height={14} width={190} /></div>}
    <div className={home || photos || item || (!items && !walk && !people) ? "pagecol" : undefined}
      data-testid="skeleton-column"
      style={people ? TEAM_PAGE : walk ? { padding: "36px 48px 56px", maxWidth: 1120, width: "100%", margin: "0 auto" }
        : item ? { width: "100%", maxWidth: columnWidth("record"), margin: "0 auto" }
        : items ? { width: "100%", maxWidth: columnWidth(wide) + 56, margin: "0 auto", padding: "0 28px" }
        : { width: "100%", maxWidth: home ? 1040 : columnWidth(wide), margin: "0 auto" }}>
      {home ? <>
        <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div className="home-hero-heading" style={{ position: "absolute", bottom: "100%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div className="home-hero-logo"><SkeletonBlock height={34} width={160} /></div>
            <div className="home-hero-skeleton-greeting" style={{ marginTop: 26 }}><SkeletonBlock height={33} width={340} /></div>
            <div className="home-hero-date" style={{ marginTop: 6, marginBottom: 26 }}><SkeletonBlock height={16} width={230} /></div>
          </div>
          <SkeletonBlock height={62} radius={18} testId="skeleton-home-input" style={{ maxWidth: CHAT_INPUT_STYLE.maxWidth }} />
          <div style={{ margin: "14px 0 26px", alignSelf: "stretch" }}><Pills /></div>
        </div>
        <ProjectsSkeleton />
        <div style={{ marginTop: 26 }}><RecentWalksSkeleton /></div>
      </> : <>
        {items || people ? <div><SkeletonBlock height={29} width={210} /><SkeletonBlock height={17} width={310} style={{ marginTop: 4 }} /></div> : item ? <SkeletonBlock height={12} width={190} /> : <Lines />}
        {photos ? <>
          <SkeletonBlock height={58} radius={18} style={{ marginTop: 16 }} />
          <div style={{ marginTop: 12, marginBottom: 22 }}><Pills count={4} /></div>
          {[0, 1].map((day) => <div key={day} style={{ border: `1px solid ${C.line}`, borderRadius: 16, background: C.surface, overflow: "hidden", marginBottom: 24 }}>
            <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${C.line}` }}>
              <SkeletonBlock height={46} width={46} radius={12} /><div style={{ flex: 1 }}><SkeletonBlock height={20} width={140} /><SkeletonBlock height={10} width={170} style={{ marginTop: 6 }} /></div>
              <SkeletonBlock height={32} width="24%" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${PHOTO_TILE_MIN}px, 1fr))`, gap: 10, padding: "14px 20px 20px" }}>
              {Array.from({ length: 6 }, (_, i) => <SkeletonBlock key={i} height="auto" radius={10} testId="skeleton-photo-tile" style={{ aspectRatio: "4 / 3" }} />)}
            </div>
          </div>)}
        </> : item ? <>
          <div style={{ marginTop: 18 }}><Pills /></div>
          <SkeletonBlock height={36} width="65%" style={{ margin: "12px 0 22px" }} />
          <div className="dlcols" style={{ display: "grid", gridTemplateColumns: ITEM_DETAIL_COLUMNS, gap: 20, alignItems: "start" }}>
            <div><SkeletonCard rows={6} rowHeight={52} /><div style={{ marginTop: 20 }}><SkeletonCard rows={2} rowHeight={42} /></div></div>
            <SkeletonBlock height={280} radius={16} />
          </div>
        </> : walk ? <>
          <div style={{ margin: "22px 0 28px" }}><Pills count={4} /></div>
          <div style={{ display: "grid", gridTemplateColumns: WALK_COLUMNS, gap: 24 }}>
            <div><SkeletonBlock height={230} radius={16} /><div style={{ marginTop: 24 }}><SkeletonCard rows={3} /></div></div>
            <SkeletonCard rows={8} rowHeight={48} />
          </div>
        </> : people ? <>
          <SkeletonBlock height={82} radius={14} />
          <TableSkeleton />
        </> : items ? <>
          <div style={{ marginTop: 22, marginBottom: 18, display: "flex", gap: 10 }}>
            <SkeletonBlock height={48} width="32%" radius={12} /><SkeletonBlock height={48} width="32%" radius={12} />
          </div>
          {shape === "items-flow" && <div style={{ padding: "22px 24px", background: C.surface, borderRadius: 16, border: `1px solid ${C.line}`, marginBottom: 22 }}>
            <SkeletonBlock height={20} width={260} /><SkeletonBlock height={34} style={{ marginTop: 18 }} /><SkeletonBlock height={10} style={{ marginTop: 10 }} />
          </div>}
          <TableSkeleton grouped={shape === "items"} />
        </> : shape === "settings" || shape === "policies" ? <>
          {shape === "policies" && <SkeletonBlock height={66} radius={10} style={{ marginTop: 18 }} />}
          {[2, 6, 3].map((rows, i) => <div key={i} style={{ marginTop: 18, marginBottom: 16 }}>
            <SkeletonBlock height={10} width={120} style={{ marginBottom: 12 }} /><SkeletonCard rows={rows} rowHeight={48} />
          </div>)}
        </> : shape === "invite" ? <>
          <SkeletonBlock height={17} width={150} style={{ margin: "8px 0 18px" }} />
          <SkeletonCard rows={4} rowHeight={60} />
          <div style={{ marginTop: 26 }}><SkeletonCard rows={3} rowHeight={48} /></div>
        </> : shape === "member" ? <>
          <SkeletonBlock height={12} width={110} style={{ margin: "24px 0 14px" }} />
          <SkeletonTiles count={3} height={100} />
          <div style={{ marginTop: 26 }}><SkeletonBlock height={10} width={150} style={{ marginBottom: 12 }} />
            <div style={{ height: 120, background: C.surface, borderRadius: 16, padding: 22, display: "flex", alignItems: "flex-end", gap: 12 }}>
              {[30, 45, 20, 60, 50, 35, 65].map((height, i) => <SkeletonBlock key={i} height={height} style={{ flex: 1 }} />)}
            </div>
          </div>
          <div style={{ marginTop: 26 }}><SkeletonCard rows={4} rowHeight={30} /></div>
          <div style={{ marginTop: 26 }}><SkeletonCard rows={3} rowHeight={48} /></div>
        </> : <>
          <div style={{ margin: "18px 0 26px" }}><Pills count={3} /></div>
          {shape === "project" ? <SkeletonBlock height={96} radius={14} /> : <SkeletonTiles count={4} height={100} />}
          <div style={{ marginTop: 26, display: "grid", gridTemplateColumns: shape === "project" ? "minmax(0,1.6fr) minmax(0,1fr)" : "1fr", gap: 14 }}>
            <SkeletonCard rows={4} /><SkeletonCard rows={3} />
            {shape === "project-brief" && <SkeletonCard rows={4} />}
          </div>
        </>}
      </>}
    </div>
  </div>;
}
